// src/components/layout/Navigation.tsx - NAVEGACIÓN ACTUALIZADA
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X, ShoppingCart, User, LogIn, LogOut } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useStrings } from '../../hooks/useStrings';

const NAV_LINK_CLASS =
  'relative text-infinity-white font-semibold hover:text-infinity-yellow transition-colors after:content-[\'\'] after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-infinity-yellow after:transition-all after:duration-300 hover:after:w-full';

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const { itemCount } = useCart();
  const { user, logout } = useAuth();
  const { strings, loading } = useStrings();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading || !strings) return null;

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <nav
      className={`bg-infinity-gradient text-infinity-white sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-2xl backdrop-blur-md bg-opacity-95' : 'shadow-lg'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center py-4">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="w-16 h-16 bg-infinity-white rounded-full flex items-center justify-center overflow-hidden transition-transform duration-300 group-hover:scale-105">
                <img
                  src="/images/01-homepage/logoinfinitywits.svg"
                  alt={strings.topBar.logoAlt}
                  className="w-24 h-24 object-cover"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-infinity-white">{strings.topBar.companyName}</h1>
                <p className="text-xs text-infinity-yellow">{strings.topBar.companyTagline}</p>
              </div>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className={NAV_LINK_CLASS}>
              {strings.topBar.home}
            </Link>


            <Link to="/servicios" className={NAV_LINK_CLASS}>
              {strings.topBar.services}
            </Link>

            <Link to="/capacitaciones" className={NAV_LINK_CLASS}>
              {strings.topBar.trainings}
            </Link>

            <Link to="/nosotros" className={NAV_LINK_CLASS}>
              {strings.topBar.about || 'Nosotros'}
            </Link>

            <Link to="/contacto" className={NAV_LINK_CLASS}>
              {strings.topBar.contact}
            </Link>

            <Link
              to="/masterclass"
              className="rounded-xl bg-infinity-yellow px-4 py-2 text-sm font-semibold text-infinity-black shadow-glow transition-transform duration-300 hover:scale-105"
            >
              Masterclass Gratis
            </Link>
          </div>

          {/* Icons */}
          <div className="flex items-center space-x-4">
            {/* Carrito 
            <Link 
              to="/carrito" 
              className="relative p-2 text-infinity-white hover:text-infinity-yellow transition-colors"
            >
              <ShoppingCart size={24} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {itemCount > 99 ? strings.topBar.cartLimit : itemCount}
                </span>
              )}
            </Link>
            */}

            {/* Usuario 
            <div className="relative">
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 p-2 text-infinity-white hover:text-infinity-yellow transition-colors"
                  >
                    <User size={24} />
                    <span className="hidden lg:block font-medium">{user.name}</span>
                    <ChevronDown size={16} />
                  </button>
                  
                  {isUserMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white shadow-lg rounded-lg py-2 z-50">
                      <Link
                        to="/perfil"
                        className="block px-4 py-2 text-infinity-white hover:bg-infinity-yellow hover:text-infinity-black transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        {strings.topBar.myProfile}
                      </Link>
                      <hr className="my-2" />
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-red-600 hover:bg-infinity-yellow hover:text-infinity-black transition-colors flex items-center space-x-2"
                      >
                 
                      <LogOut size={16} />
                        <span>{strings.topBar.logout}</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link 
                  to="/login" 
                  className="flex items-center space-x-2 p-2 text-infinity-white hover:text-infinity-yellow transition-colors"
                >
                  <LogIn size={24} />
                  <span className="hidden lg:block font-medium">{strings.topBar.login}</span>
                </Link>
              )}
            </div>
              */}
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-infinity-white hover:text-infinity-yellow transition-transform duration-200 active:scale-90"
              >
                <span
                  className={`inline-block transition-transform duration-300 ${isMenuOpen ? 'rotate-90' : 'rotate-0'}`}
                >
                  {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 animate-fade-in-down">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                {strings.topBar.home}
              </Link>
              <Link
                to="/productos"
                className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                {strings.topBar.products}
              </Link>
              <Link
                to="/servicios"
                className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                {strings.topBar.services}
              </Link>
              <Link
                to="/capacitaciones"
                className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                {strings.topBar.trainings}
              </Link>
              <Link
                to="/nosotros"
                className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                {strings.topBar.about || 'Nosotros'}
              </Link>
              <Link
                to="/contacto"
                className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                {strings.topBar.contact}
              </Link>

              <Link
                to="/masterclass"
                className="w-fit rounded-xl bg-infinity-yellow px-4 py-2 text-sm font-semibold text-infinity-black shadow-glow transition-transform hover:scale-105"
                onClick={() => setIsMenuOpen(false)}
              >
                Clase Gratis
              </Link>

              <hr className="my-2 border-white/10" />

              {user ? (
                <>
                  <Link
                    to="/perfil"
                    className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {strings.topBar.myProfileMobile}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="text-left text-red-500 font-semibold hover:text-red-400 hover:translate-x-1 transition-all"
                  >
                    {strings.topBar.logoutMobile}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="text-infinity-white font-semibold hover:text-infinity-yellow hover:translate-x-1 transition-all"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {strings.topBar.loginMobile}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
